/**
 * ФОТ строки техкарты по ролям + прямые суммы по статьям (ADR-071 D11, D5).
 *
 * Формула — из эталона Ольховатки, сверена построчно:
 *   (ставка × (1 + вредность) + премия) × (1 + ЕСН) × объём работ.
 * Вредность — процент к базовой ставке; премия — сумма на единицу объёма ЛИБО
 * процент от ставки (одно из двух); ЕСН начисляется на сумму.
 *
 * Наличие хотя бы одной роли отключает старую расценку строки — двойного
 * счёта ФОТ не бывает. Прямая сумма по статье ЗАМЕЩАЕТ расчётную часть этой
 * статьи («Пожарные мероприятия»: ФОТ 10 000 ₽ суммой, без расценок).
 */
import { useState } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  ARTICLE_ORDER,
  ARTICLE_TITLES,
  LABOR_ROLE_TITLES,
  PAY_FORM_TITLES,
  type CostArticle,
  type TechCardDirectCost,
  type TechCardLaborRow,
  type TechCardOperation,
} from '@/lib/planning/techCards';

interface LaborDraft {
  labor_role: TechCardLaborRow['labor_role'];
  pay_form: TechCardLaborRow['pay_form'];
  base_rate: string;
  bonus_mode: 'rate' | 'pct';
  bonus: string;
  hazard_pct: string;
  payroll_tax_pct: string;
}

interface DirectDraft {
  article: CostArticle;
  amount: string;
}

/** Пустая строка → null, «1,5» → 1.5, мусор → undefined. */
function parseNumber(raw: string): number | null | undefined {
  const trimmed = raw.trim().replace(/\s/g, '').replace(',', '.');
  if (!trimmed) return null;
  const value = Number(trimmed);
  if (!Number.isFinite(value) || value < 0) return undefined;
  return value;
}

function toDraft(row: TechCardLaborRow): LaborDraft {
  return {
    labor_role: row.labor_role,
    pay_form: row.pay_form,
    base_rate: row.base_rate == null ? '' : String(row.base_rate),
    bonus_mode: row.bonus_pct != null ? 'pct' : 'rate',
    bonus:
      row.bonus_pct != null ? String(row.bonus_pct) : row.bonus_rate != null ? String(row.bonus_rate) : '',
    hazard_pct: row.hazard_pct ? String(row.hazard_pct) : '',
    payroll_tax_pct: row.payroll_tax_pct ? String(row.payroll_tax_pct) : '',
  };
}

export function LaborDialog({
  operation,
  saving,
  onSubmit,
  onClose,
}: {
  operation: TechCardOperation;
  saving: boolean;
  onSubmit: (values: { labor: TechCardLaborRow[]; directCosts: TechCardDirectCost[] }) => void;
  onClose: () => void;
}) {
  const [rows, setRows] = useState<LaborDraft[]>((operation.labor ?? []).map(toDraft));
  const [direct, setDirect] = useState<DirectDraft[]>(
    (operation.direct_costs ?? []).map((d) => ({ article: d.article, amount: String(d.amount) })),
  );
  const [error, setError] = useState<string | null>(null);

  const usedRoles = new Set(rows.map((r) => r.labor_role));
  const usedArticles = new Set(direct.map((d) => d.article));
  const title = operation.name ?? operation.op_type_name ?? 'операция без названия';

  function patchRow(index: number, patch: Partial<LaborDraft>) {
    setRows((cur) => cur.map((row, i) => (i === index ? { ...row, ...patch } : row)));
  }

  function submit() {
    const labor: TechCardLaborRow[] = [];
    for (const row of rows) {
      const base = parseNumber(row.base_rate);
      const bonus = parseNumber(row.bonus);
      const hazard = parseNumber(row.hazard_pct);
      const tax = parseNumber(row.payroll_tax_pct);
      if ([base, bonus, hazard, tax].some((v) => v === undefined)) {
        setError('Числовые поля ФОТ принимают только неотрицательные числа.');
        return;
      }
      labor.push({
        labor_role: row.labor_role,
        persons_per_unit: 1,
        pay_form: row.pay_form,
        base_rate: base ?? null,
        rate_basis: null,
        bonus_rate: row.bonus_mode === 'rate' ? (bonus ?? null) : null,
        bonus_pct: row.bonus_mode === 'pct' ? (bonus ?? null) : null,
        hazard_pct: hazard ?? 0,
        payroll_tax_pct: tax ?? 0,
      });
    }
    const directCosts: TechCardDirectCost[] = [];
    for (const d of direct) {
      const amount = parseNumber(d.amount);
      if (amount === undefined || amount === null) {
        setError('У прямой суммы обязана быть сумма в рублях.');
        return;
      }
      directCosts.push({ article: d.article, amount });
    }
    setError(null);
    onSubmit({ labor, directCosts });
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Оплата труда: {title}</DialogTitle>
          <DialogDescription>
            Расценка — на одного человека за единицу объёма работ. Вредность — процент к
            базовой расценке, ЕСН начисляется на сумму со вредностью и премией. Пустая
            расценка — берётся из справочника расценок сезона.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {rows.length === 0 && (
            <p className="text-sm text-muted-foreground">
              Ролей пока нет — оплата считается по старой расценке строки или нормативу
              сезона. Добавьте роль, чтобы задать премию, вредность и ЕСН.
            </p>
          )}

          {rows.map((row, index) => (
            <div key={row.labor_role} className="rounded-md border border-border p-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium text-foreground">
                  {LABOR_ROLE_TITLES[row.labor_role]}
                </span>
                <div className="ml-auto flex items-center gap-2">
                  {(Object.keys(PAY_FORM_TITLES) as TechCardLaborRow['pay_form'][]).map((form) => (
                    <button
                      key={form}
                      type="button"
                      aria-pressed={row.pay_form === form}
                      onClick={() => patchRow(index, { pay_form: form })}
                      className={
                        row.pay_form === form
                          ? 'rounded-full border border-primary bg-primary/10 px-3 py-1 text-xs font-medium text-primary transition-colors'
                          : 'rounded-full border border-border px-3 py-1 text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-foreground'
                      }
                    >
                      {PAY_FORM_TITLES[form]}
                    </button>
                  ))}
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label={`Убрать роль ${LABOR_ROLE_TITLES[row.labor_role]}`}
                    onClick={() => setRows((cur) => cur.filter((_, i) => i !== index))}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              {row.pay_form === 'hourly' && (
                <p className="mt-1 text-xs text-muted-foreground">
                  Повременная считается от нормо-часов — без нормы выработки строка честно
                  покажет «не посчитано», а не ноль.
                </p>
              )}
              <div className="mt-2 grid gap-3 sm:grid-cols-4">
                <div>
                  <Label htmlFor={`rate-${row.labor_role}`}>Расценка, ₽/ед</Label>
                  <Input
                    id={`rate-${row.labor_role}`}
                    inputMode="decimal"
                    value={row.base_rate}
                    onChange={(e) => patchRow(index, { base_rate: e.target.value })}
                    placeholder="из справочника"
                  />
                </div>
                <div>
                  <Label htmlFor={`bonus-${row.labor_role}`}>
                    Премия, {row.bonus_mode === 'pct' ? '%' : '₽/ед'}
                  </Label>
                  <div className="flex gap-1">
                    <Input
                      id={`bonus-${row.labor_role}`}
                      inputMode="decimal"
                      value={row.bonus}
                      onChange={(e) => patchRow(index, { bonus: e.target.value })}
                    />
                    <button
                      type="button"
                      title="Премия — суммой на единицу объёма либо процентом от ставки"
                      onClick={() =>
                        patchRow(index, { bonus_mode: row.bonus_mode === 'pct' ? 'rate' : 'pct' })
                      }
                      className="rounded-md border border-border px-2 text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                    >
                      {row.bonus_mode === 'pct' ? '%' : '₽'}
                    </button>
                  </div>
                </div>
                <div>
                  <Label htmlFor={`hazard-${row.labor_role}`}>Вредность, %</Label>
                  <Input
                    id={`hazard-${row.labor_role}`}
                    inputMode="decimal"
                    value={row.hazard_pct}
                    onChange={(e) => patchRow(index, { hazard_pct: e.target.value })}
                  />
                </div>
                <div>
                  <Label htmlFor={`tax-${row.labor_role}`}>ЕСН, %</Label>
                  <Input
                    id={`tax-${row.labor_role}`}
                    inputMode="decimal"
                    value={row.payroll_tax_pct}
                    onChange={(e) => patchRow(index, { payroll_tax_pct: e.target.value })}
                    placeholder="напр. 29,2"
                  />
                </div>
              </div>
            </div>
          ))}

          <div className="flex flex-wrap gap-2">
            {(Object.keys(LABOR_ROLE_TITLES) as TechCardLaborRow['labor_role'][])
              .filter((role) => !usedRoles.has(role))
              .map((role) => (
                <Button
                  key={role}
                  size="sm"
                  variant="outline"
                  onClick={() =>
                    setRows((cur) => [
                      ...cur,
                      {
                        labor_role: role,
                        pay_form: 'piece',
                        base_rate: '',
                        bonus_mode: 'rate',
                        bonus: '',
                        hazard_pct: '',
                        payroll_tax_pct: '',
                      },
                    ])
                  }
                >
                  <Plus className="mr-2 h-4 w-4" />
                  {LABOR_ROLE_TITLES[role]}
                </Button>
              ))}
          </div>

          <div className="border-t border-border pt-3">
            <div className="text-sm font-semibold text-foreground">Прямые суммы по статьям</div>
            <p className="mt-1 text-xs text-muted-foreground">
              Для работ без расценок и норм («Пожарные мероприятия»): сумма по статье
              задаётся напрямую и замещает расчёт этой статьи на строке.
            </p>
            <div className="mt-2 space-y-2">
              {direct.map((d, index) => (
                <div key={d.article} className="flex flex-wrap items-center gap-2">
                  <span className="w-36 text-sm text-muted-foreground">
                    {ARTICLE_TITLES[d.article]}
                  </span>
                  <Input
                    aria-label={`Сумма по статье ${ARTICLE_TITLES[d.article]}`}
                    inputMode="decimal"
                    className="w-40"
                    value={d.amount}
                    onChange={(e) =>
                      setDirect((cur) =>
                        cur.map((row, i) => (i === index ? { ...row, amount: e.target.value } : row)),
                      )
                    }
                  />
                  <span className="text-sm text-muted-foreground">₽ на всю строку</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label={`Убрать прямую сумму ${ARTICLE_TITLES[d.article]}`}
                    onClick={() => setDirect((cur) => cur.filter((_, i) => i !== index))}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              {ARTICLE_ORDER.filter((article) => !usedArticles.has(article)).map((article) => (
                <button
                  key={article}
                  type="button"
                  onClick={() => setDirect((cur) => [...cur, { article, amount: '' }])}
                  className="rounded-full border border-border px-3 py-1 text-xs text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                >
                  + {ARTICLE_TITLES[article]}
                </button>
              ))}
            </div>
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Отмена
          </Button>
          <Button onClick={submit} disabled={saving}>
            Сохранить
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
